import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useChatContext } from "@/context/ChatContext";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useUser } from "@/context/UserProvider";
import { useStaffReports } from "@/context/StaffReportsContext";
import { usePlanReview } from "@/context/PlanReviewContext";
import { listStaffReportDrafts } from "@/api/staff_reports";
import { listMunicipalityResourceLinks } from "@/api/municipality_resource_links";
import { getFileLibrarySummary } from "@/api/file_library";
import { fetchLaunchPadImpactStats } from "@/api/launchPad";
import { RESUME_LIMITS } from "@/components/launch_pad/launchPadConfig";
import type { ActiveSection, LaunchPadNavigateOptions } from "@/types/appNavigation";

export type ResumeItem = {
  id: string;
  title: string;
  moduleLabel: string;
  section: ActiveSection;
  relativeTime: string;
  updatedAt: number;
  navigateOptions: LaunchPadNavigateOptions;
};

function buildResumeItem(
  partial: Omit<ResumeItem, "relativeTime" | "updatedAt"> & { updatedAt: string | number },
): ResumeItem {
  const updatedAtMs = new Date(partial.updatedAt).getTime();
  return {
    ...partial,
    updatedAt: updatedAtMs,
    relativeTime: formatDistanceToNow(updatedAtMs, { addSuffix: true }),
  };
}

export function useLaunchPadDashboard() {
  const { currentUser } = useUser();
  const { municipality } = useMunicipality();
  const { chats, isLoading: chatsLoading } = useChatContext();
  const { drafts, setDrafts } = useStaffReports();
  const { reviews, refreshReviews } = usePlanReview();
  const [draftsLoading, setDraftsLoading] = useState(false);

  const municipalityId = municipality?.id;

  const { data: impactStats, isLoading: impactLoading } = useQuery({
    queryKey: ["launchPadImpactStats", municipalityId],
    queryFn: () => fetchLaunchPadImpactStats(municipalityId),
    enabled: Boolean(currentUser),
    staleTime: 1000 * 60 * 2,
  });

  useEffect(() => {
    if (!municipalityId || !currentUser?.staff_reports_access) return;
    let cancelled = false;
    setDraftsLoading(true);
    listStaffReportDrafts({ municipalityId })
      .then((list) => {
        if (!cancelled) setDrafts(list.drafts);
      })
      .catch(() => {
        if (!cancelled) setDrafts([]);
      })
      .finally(() => {
        if (!cancelled) setDraftsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [municipalityId, currentUser?.staff_reports_access, setDrafts]);

  useEffect(() => {
    if (municipalityId && currentUser?.plan_review_access) {
      refreshReviews();
    }
  }, [municipalityId, currentUser?.plan_review_access, refreshReviews]);

  const { data: resourceLinks = [], isLoading: linksLoading } = useQuery({
    queryKey: ["launchPadResourceLinks", municipalityId],
    queryFn: () => listMunicipalityResourceLinks(municipalityId!),
    enabled: Boolean(municipalityId && currentUser?.municipality_resource_links_access),
    staleTime: 1000 * 60 * 5,
  });

  const { data: fileLibrary, isLoading: fileLibraryLoading } = useQuery({
    queryKey: ["launchPadFileLibrarySummary", municipalityId],
    queryFn: () => getFileLibrarySummary(municipalityId!),
    enabled: Boolean(municipalityId && currentUser?.file_viewer),
    staleTime: 1000 * 60 * 5,
  });

  const recentFeed = useMemo((): ResumeItem[] => {
    if (!municipalityId || !currentUser) return [];

    const items: ResumeItem[] = [];

    chats
      // Opportunity research chats live on their opportunity's Research tab and
      // have no municipality-RA route to resume into; they share the chats index
      // with the RA, so they have to be filtered out here.
      .filter(
        (c) =>
          c.discarded_at === null &&
          c.municipality_id === municipalityId &&
          c.kind !== "opportunity_research",
      )
      .forEach((c) => {
        items.push(
          buildResumeItem({
            id: `chat-${c.id}`,
            title: c.title || "Untitled conversation",
            moduleLabel: "Research Assistant",
            section: "chat",
            updatedAt: c.updated_at || c.created_at,
            navigateOptions: { chatId: c.id, chatKind: "research_assistant" },
          }),
        );
      });

    if (currentUser.staff_reports_access) {
      drafts
        .filter((d) => !d.discarded_at)
        .forEach((d) => {
          items.push(
            buildResumeItem({
              id: `draft-${d.id}`,
              title: d.name,
              moduleLabel: "Report Assistant",
              section: "staff_reports",
              updatedAt: d.updated_at,
              navigateOptions: { staffReportDraftId: d.id },
            }),
          );
        });
    }

    if (currentUser.plan_review_access) {
      reviews.forEach((r) => {
        items.push(
          buildResumeItem({
            id: `plan-${r.id}`,
            title: r.title || "Untitled plan review",
            moduleLabel: "Plan Review",
            section: "plan_review",
            updatedAt: r.updated_at || r.created_at,
            navigateOptions: { planReviewId: r.id },
          }),
        );
      });
    }

    return items.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, RESUME_LIMITS.recentFeed);
  }, [chats, currentUser, drafts, municipalityId, reviews]);

  const activeResourceLinks = useMemo(
    () =>
      resourceLinks
        .filter((l) => l.active)
        .sort((a, b) => a.priority - b.priority || a.title.localeCompare(b.title))
        .slice(0, 4),
    [resourceLinks],
  );

  const fileLibrarySummary = useMemo(() => {
    if (!fileLibrary) return null;
    return {
      documentCount: fileLibrary.document_count,
      folderCount: fileLibrary.folder_count,
    };
  }, [fileLibrary]);

  const isLoading = chatsLoading || draftsLoading;

  return {
    municipalityId,
    municipality,
    isLoading,
    impactStats,
    impactLoading,
    linksLoading,
    fileLibraryLoading,
    recentFeed,
    activeResourceLinks,
    fileLibrarySummary,
  };
}
